// src/pages/PortalSaaS/modules/DashboardCRM.js
import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { db } from '../../../firebase';
import { doc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';

const DashboardCRM = () => {
    const { workspaceData } = useOutletContext();
    const navigate = useNavigate();
    
    // --- ESTADOS BASE (IA E EQUIPE) ---
    const [saldoRealTime, setSaldoRealTime] = useState(workspaceData?.config_ia?.saldoInteracoesIA || 0);
    const [isAutoRecharge, setIsAutoRecharge] = useState(workspaceData?.config_ia?.autoRecharge || false);

    // --- NOVOS ESTADOS: MÉTRICAS REAIS DO CRM ---
    const [metricas, setMetricas] = useState({
        laudosGerados: '...',
        inconsistencias: '...',
        faturamentoReceber: 0
    });

    // --- NOVOS ESTADOS: FUNIL DINÂMICO ---
    const [funilAguardando, setFunilAguardando] = useState([]);
    const [funilEmAuditoria, setFunilEmAuditoria] = useState([]);
    const [funilAprovados, setFunilAprovados] = useState([]);

    // Permissão Visual e Anti-Hacker
    const isOwnerVisual = workspaceData?.role === 'OWNER' || workspaceData?.permissao_global === true;
    const isExtranetView = workspaceData?.role === 'EXTRANET';

    // 1. ESCUTADOR DE SALDO EM TEMPO REAL
    useEffect(() => {
        if (!workspaceData?.cnpj_completo || isExtranetView) return; // Cliente não vê saldo do Lab
        
        const wkspRef = doc(db, 'workspaces', workspaceData.cnpj_completo);
        const unsubscribe = onSnapshot(wkspRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setSaldoRealTime(data.config_ia?.saldoInteracoesIA || 0);
                setIsAutoRecharge(data.config_ia?.autoRecharge || false);
            }
        });

        return () => unsubscribe(); 
    }, [workspaceData, isExtranetView]);

    // 2. BUSCADOR DO CENSO DA EQUIPE E MÉTRICAS DO CRM
    useEffect(() => {
        if (!workspaceData?.cnpj_completo) return;

        const carregarDashboard = async () => {
            try {
                // --- A: CENSO DA EQUIPE (Apenas Internos) ---
                if (!isExtranetView && workspaceData?.cnpj_raiz) {
                    const qEquipe = query(
                        collection(db, 'usuarios_workspace'), 
                        where('cnpj_raiz', '==', workspaceData.cnpj_raiz),
                        where('status', '==', 'ACTIVE')
                    );
                    const snapEquipe = await getDocs(qEquipe); 
                    const uidsUnicos = new Set();
                    snapEquipe.forEach(doc => uidsUnicos.add(doc.data().uid));
                }

                // --- B: MÉTRICAS E FUNIL DOS TICKETS (LAUDOS E ENSAIOS) ---
                // Se for Extranet, a query usa a trava 'cliente_cnpj_visibilidade' criada na Amostras.js
                // O Firebase Rules rejeitaria a query aberta para a Extranet, por isso a distinção.
                const baseTicketsQuery = isExtranetView 
                    ? query(collection(db, 'workspaces', workspaceData.cnpj_completo, 'crm_tickets'), where('cliente_cnpj_visibilidade', '==', workspaceData.documento_fiscal))
                    : query(collection(db, 'workspaces', workspaceData.cnpj_completo, 'crm_tickets'));
                
                const snapTickets = await getDocs(baseTicketsQuery);
                let countLaudos = 0;
                let countInconsistencias = 0;
                let faturamentoPendente = 0;

                const listaAguardando = [];
                const listaAuditoria = [];
                const listaAprovados = [];

                snapTickets.forEach(docSnap => {
                    const ticket = { id: docSnap.id, ...docSnap.data() };

                    // Contabilização de Métricas
                    if (ticket.status === 'LAUDO_EMITIDO' || ticket.status === 'AGUARDANDO_PAGAMENTO') countLaudos++;
                    if (ticket.status === 'REPROVADO') countInconsistencias++;
                    
                    // Somatória de Faturamento (O Laboratório só fatura se cobrar via Central)
                    // A Cloud Function grava o valor do boleto/pix gerado na variável valor_cobranca (ou valor_venda do front)
                    if (!isExtranetView && ticket.status === 'AGUARDANDO_PAGAMENTO') {
                        const valor = ticket.valor_cobranca || ticket.valor_venda || 0;
                        faturamentoPendente += Number(valor);
                    }

                    // Distribuição Visual do Funil
                    if (ticket.status === 'AGUARDANDO_MAQUINA') listaAguardando.push(ticket);
                    if (ticket.status === 'EM_AUDITORIA_IA') listaAuditoria.push(ticket);
                    if (ticket.status === 'LAUDO_EMITIDO' || ticket.status === 'AGUARDANDO_PAGAMENTO') listaAprovados.push(ticket);
                });

                setMetricas({ laudosGerados: countLaudos, inconsistencias: countInconsistencias, faturamentoReceber: faturamentoPendente });
                
                // Ordenação simples no front-end para mostrar os mais recentes (últimos 3)
                // Assumindo que a collection retorna sem ordem específica ou por criação padrão
                setFunilAguardando(listaAguardando.slice(0, 3));
                setFunilEmAuditoria(listaAuditoria.slice(0, 3));
                setFunilAprovados(listaAprovados.slice(0, 3));

            } catch (e) {
                console.error("Erro ao carregar Dashboard CRM:", e);
            }
        };

        carregarDashboard();
    }, [workspaceData, isExtranetView]);


    // --- LÓGICA DE ALERTA DE CARTEIRA (WALLET GUARD) ---
    const saldoCritico = saldoRealTime < 1000;
    const saldoEsgotado = saldoRealTime < 100;

    return (
        <div className="pts-fade-in" style={{ padding: '30px', height: '100%', overflowY: 'auto', background: '#f8fafc' }}>
            <header className="pts-view-header" style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h1 style={{ color: '#0f172a', margin: '0 0 5px 0', fontSize: '1.8rem', fontWeight: '800' }}>
                        {isExtranetView ? 'Painel B2B do Cliente' : 'Dashboard de Engenharia'}
                    </h1>
                    <p style={{ color: '#64748b', margin: 0, fontSize: '0.95rem' }}>
                        Visão global do ambiente {workspaceData.tipo_unidade === 'MATRIZ' ? 'Corporativo' : 'Local'} de <strong>{workspaceData.nomeExibicao}</strong>.
                    </p>
                </div>
                <div className="pts-header-actions" style={{ display: 'flex', gap: '10px' }}>
                    <button className="pts-btn-outline" style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: '600' }}>
                        Exportar Relatórios
                    </button>
                    {!isExtranetView && (
                        <button 
                            className="pts-btn-primary" 
                            style={{ padding: '10px 20px', background: '#0369a1', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }} 
                            onClick={() => navigate(`/${workspaceData.slug}/auditoria`)}
                        >
                            + Iniciar Nova Auditoria
                        </button>
                    )}
                </div>
            </header>

            {/* BANNER DE ALERTA FINANCEIRO (Oculto para Extranet) */}
            {!isExtranetView && isOwnerVisual && (saldoCritico || saldoEsgotado) && (
                <div style={{ 
                    background: saldoEsgotado ? '#fee2e2' : '#fef3c7', 
                    border: `1px solid ${saldoEsgotado ? '#fca5a5' : '#fde68a'}`, 
                    padding: '15px 20px', borderRadius: '12px', marginBottom: '25px', 
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center' 
                }}>
                    <div>
                        <strong style={{ color: saldoEsgotado ? '#dc2626' : '#d97706', display: 'block', fontSize: '1rem' }}>
                            {saldoEsgotado ? '🚨 SALDO DE INTELIGÊNCIA ESGOTADO' : '⚠️ ATENÇÃO: NÍVEL CRÍTICO DE TOKENS'}
                        </strong>
                        <span style={{ color: saldoEsgotado ? '#991b1b' : '#b45309', fontSize: '0.85rem' }}>
                            {isAutoRecharge 
                                ? "O sistema tentará faturar a renovação automática no seu cartão cadastrado no Asaas na próxima interação."
                                : "A automação de auditoria será suspensa em breve. Adquira um novo pacote de processamento para evitar interrupções."}
                        </span>
                    </div>
                    {!isAutoRecharge && (
                        <button className="pts-btn-launch" style={{ padding: '8px 15px', fontSize: '0.8rem', width: 'auto', margin: 0, background: '#0f172a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                            💳 RECARREGAR CARTEIRA
                        </button>
                    )}
                </div>
            )}

            {/* PAINEL EXCLUSIVO DO SÓCIO */}
            {!isExtranetView && isOwnerVisual && (
                <div style={{ background: '#e0f2fe', padding: '20px', borderRadius: '16px', border: '1px solid #bae6fd', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <div>
                        <h3 style={{ color: '#0369a1', margin: '0 0 5px 0' }}>👑 Visão do Sócio-Proprietário</h3>
                        <p style={{ color: '#0c4a6e', margin: 0, fontSize: '0.9rem' }}>
                            Você gerencia a organização Raiz: <strong>{workspaceData.cnpj_raiz}</strong>.
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button 
                            className="pts-btn-primary" 
                            style={{ background: '#0369a1', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }} 
                            onClick={() => navigate(`/${workspaceData.slug}/governanca`)}
                        >
                            ⚙️ GERENCIAR FILIAIS E EQUIPE
                        </button>
                    </div>
                </div>
            )}

            {/* KPIs Rápidos (Métricas Reais) */}
            <div style={{ 
                display: 'grid', 
                // A MÁGICA DO WRAP: Se o card não couber em 220px, ele pula pra linha de baixo.
                gridTemplateColumns: `repeat(auto-fit, minmax(${isExtranetView ? '250px' : '220px'}, 1fr))`, 
                gap: '15px' 
            }}>
                <div className="pts-setup-card config" style={{ padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <h4 style={{ color: '#94a3b8', margin: '0 0 10px 0', fontSize: '0.85rem', textTransform: 'uppercase' }}>Laudos Emitidos</h4>
                    <h2 style={{ fontSize: '2rem', margin: 0, color: '#38bdf8' }}>{metricas.laudosGerados}</h2>
                </div>
                
                <div className="pts-setup-card config" style={{ padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <h4 style={{ color: '#94a3b8', margin: '0 0 10px 0', fontSize: '0.85rem', textTransform: 'uppercase' }}>Inconsistências (Revisão IA)</h4>
                    <h2 style={{ fontSize: '2rem', margin: 0, color: '#f43f5e' }}>{metricas.inconsistencias}</h2>
                </div>

                {!isExtranetView && (
                    <>
                        <div className="pts-setup-card config" style={{ padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <h4 style={{ color: '#94a3b8', margin: '0 0 10px 0', fontSize: '0.85rem', textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between' }}>
                                Faturamento a Receber (B2B)
                            </h4>
                            <h2 style={{ fontSize: '1.8rem', margin: 0, color: '#10b981' }}>
                                R$ {metricas.faturamentoReceber.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </h2>
                        </div>
                        <div className="pts-setup-card config" style={{ padding: '20px', background: '#fff', borderRadius: '12px', border: saldoCritico ? '2px solid #fca5a5' : '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <h4 style={{ color: '#94a3b8', margin: '0 0 10px 0', fontSize: '0.85rem', textTransform: 'uppercase' }}>Tokens IA Restantes</h4>
                            <h2 style={{ fontSize: '2rem', margin: 0, color: saldoCritico ? '#dc2626' : '#64748b' }}>
                                {saldoRealTime.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}
                            </h2>
                        </div>
                    </>
                )}
            </div>

            {/* Kanban Visual Vivo (Lendo do Firebase) */}
            <div style={{ marginTop: '40px' }}>
                <h3 style={{ marginBottom: '20px', color: '#0f172a' }}>Atividade Recente de Ensaios</h3>
                <div style={{ 
                    display: 'grid', 
                    // A MÁGICA DO WRAP NO KANBAN: Quebra de linha se a coluna ficar menor que 280px.
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
                    gap: '20px' 
                }}>
                    
                    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <h4 style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', color: '#64748b', margin: '0 0 15px 0' }}>⏳ Fila do Laboratório</h4>
                        {funilAguardando.length === 0 ? (
                            <p style={{ fontSize: '0.85rem', color: '#94a3b8', textAlign: 'center', marginTop: '30px' }}>Sem demanda imediata.</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {funilAguardando.map(ticket => (
                                    <div key={ticket.id} style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.85rem' }}>
                                        <strong style={{ color: '#0f172a' }}>{ticket.tipo_ensaio}</strong><br/>
                                        <span style={{ color: '#64748b', fontSize: '0.75rem' }}>Amostra: {ticket.amostra_id}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #bae6fd', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <h4 style={{ borderBottom: '1px solid #e0f2fe', paddingBottom: '10px', color: '#0369a1', margin: '0 0 15px 0' }}>🤖 Em Auditoria (IA)</h4>
                        {funilEmAuditoria.length === 0 ? (
                            <p style={{ fontSize: '0.85rem', color: '#0ea5e9', textAlign: 'center', marginTop: '30px' }}>IA ociosa.</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {funilEmAuditoria.map(ticket => (
                                    <div key={ticket.id} style={{ background: '#e0f2fe', padding: '12px', borderRadius: '6px', border: '1px solid #bae6fd', fontSize: '0.85rem' }}>
                                        <strong style={{ color: '#0369a1' }}>{ticket.tipo_ensaio}</strong><br/>
                                        <span style={{ color: '#0284c7', fontSize: '0.75rem' }}>Processando Anomalias...</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #86efac', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <h4 style={{ borderBottom: '1px solid #dcfce7', paddingBottom: '10px', color: '#16a34a', margin: '0 0 15px 0' }}>✅ Laudos Liberados</h4>
                        {funilAprovados.length === 0 ? (
                            <p style={{ fontSize: '0.85rem', color: '#22c55e', textAlign: 'center', marginTop: '30px' }}>Nenhuma entrega recente.</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {funilAprovados.map(ticket => (
                                    <div key={ticket.id} style={{ background: ticket.status === 'AGUARDANDO_PAGAMENTO' ? '#fef3c7' : '#dcfce7', padding: '12px', borderRadius: '6px', border: `1px solid ${ticket.status === 'AGUARDANDO_PAGAMENTO' ? '#fde68a' : '#86efac'}`, fontSize: '0.85rem' }}>
                                        <strong style={{ color: '#0f172a' }}>{ticket.tipo_ensaio}</strong><br/>
                                        <span style={{ color: ticket.status === 'AGUARDANDO_PAGAMENTO' ? '#d97706' : '#16a34a', fontWeight: 'bold', fontSize: '0.75rem' }}>
                                            {ticket.status === 'AGUARDANDO_PAGAMENTO' ? 'Aguardando Pagamento' : 'PDF Disponível'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                </div>
            </div>
            
        </div>
    );
};

export default DashboardCRM;